import React from 'react'
import { useTranslation } from 'react-i18next'
import { Dumbbell, Bed } from 'lucide-react'
import { Card, CardContent } from '../../../../components/ui/card'

function fmt(n, digits = 0) {
  if (n == null) return '—'
  return Number(n).toFixed(digits)
}

export function TrainingRestMatrix({ stats }) {
  const { t } = useTranslation()

  const hasAny = stats.training.days > 0 || stats.rest.days > 0
  if (!hasAny) {
    return (
      <Card>
        <CardContent className="p-4 text-center text-xs text-muted-foreground" dir="auto">
          {t('phoenixTracker.analysis.matrix.empty')}
        </CardContent>
      </Card>
    )
  }

  // kcalBurned only renders when at least one side has training data.
  // Strength + cardio sessions both contribute via sessionTotalKcal.
  const hasBurn = (stats.training.kcalBurned ?? 0) > 0 || (stats.rest.kcalBurned ?? 0) > 0
  const rows = [
    { key: 'kcal', digits: 0 },
    { key: 'protein', digits: 0 },
    { key: 'carbs', digits: 0 },
    { key: 'fat', digits: 0 },
    ...(hasBurn ? [{ key: 'kcalBurned', digits: 0 }] : []),
  ]

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Dumbbell className="w-4 h-4 text-app-primary-500" />
          <h3 className="text-sm font-semibold text-foreground" dir="auto">
            {t('phoenixTracker.analysis.matrix.title')}
          </h3>
        </div>

        <div className="overflow-x-auto" dir="ltr">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="text-start font-medium pb-2"></th>
                <th className="text-end font-medium pb-2">
                  <span className="inline-flex items-center gap-1">
                    <Dumbbell className="w-3 h-3" />
                    {t('phoenixTracker.analysis.matrix.training')}
                  </span>
                </th>
                <th className="text-end font-medium pb-2">
                  <span className="inline-flex items-center gap-1">
                    <Bed className="w-3 h-3" />
                    {t('phoenixTracker.analysis.matrix.rest')}
                  </span>
                </th>
              </tr>
              <tr className="text-[10px] text-muted-foreground/80">
                <td className="pb-2"></td>
                <td className="text-end font-mono pb-2">
                  {stats.training.days} {t('phoenixTracker.analysis.matrix.daysShort')}
                </td>
                <td className="text-end font-mono pb-2">
                  {stats.rest.days} {t('phoenixTracker.analysis.matrix.daysShort')}
                </td>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ key, digits }) => (
                <tr key={key} className="border-t border-border/50">
                  <td className="py-2 text-foreground" dir="auto">
                    {t(`phoenixTracker.analysis.matrix.${key}`)}
                  </td>
                  <td className="py-2 text-end font-mono text-foreground">
                    {fmt(stats.training[key], digits)}
                  </td>
                  <td className="py-2 text-end font-mono text-foreground">
                    {fmt(stats.rest[key], digits)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
